import { Injectable, computed, inject, signal } from '@angular/core';
import { Subject, fromEvent, merge } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { LembreteService } from './lembrete.service';
import { SocketService } from './socket.service';
import { AuthService } from './auth.service';
import { LembreteNotificacao } from '../model/lembrete/lembrete-notificacao.model';
import { LembreteNotificacaoSocket } from '../model/socket/lembrete-notificacao-socket.model';
import { EtapaAvisoLembreteEnum } from '../enum/etapa-aviso-lembrete.enum';
import { StatusLembreteEnum } from '../enum/status-lembrete.enum';
import {
  ATAQUE_SOM_LEMBRETE_S, DURACAO_NOTA_SOM_LEMBRETE_S, GANHO_MINIMO_SOM_LEMBRETE, JANELA_SOM_LEMBRETE_MS, MINUTOS_ADIAMENTO_PADRAO_LEMBRETE,
  NOTAS_SOM_LEMBRETE, PREFIXO_CHAVE_SOM_LEMBRETE, VOLUME_SOM_LEMBRETE
} from '../util/lembrete-notificacao.constantes';

@Injectable({ providedIn: 'root' })
export class LembreteNotificacaoService {
  private readonly lembreteService = inject(LembreteService);
  private readonly socketService = inject(SocketService);
  private readonly authService = inject(AuthService);

  private readonly lista = signal<LembreteNotificacao[]>([]);
  readonly notificacoes = this.lista.asReadonly();
  readonly total = computed(() => this.lista().length);

  private iniciado = false;
  private encerrar$ = new Subject<void>();
  private contextoAudio?: AudioContext;

  aoNavegar(): void {
    if (!this.authService.logado()) return;
    if (!this.iniciado) {
      this.iniciar();
      return;
    }
    this.sincronizar();
  }

  encerrar(): void {
    if (!this.iniciado) return;
    this.iniciado = false;
    this.encerrar$.next();
    this.encerrar$.complete();
    this.lista.set([]);
  }

  podeAgir(notificacao: LembreteNotificacao): boolean {
    return notificacao.status === StatusLembreteEnum.PENDENTE
      && notificacao.etapaAviso !== EtapaAvisoLembreteEnum.EXECUCAO;
  }

  adiar(id: number, minutos = MINUTOS_ADIAMENTO_PADRAO_LEMBRETE): void {
    this.remover(id);
    this.lembreteService.adiar(id, minutos).subscribe({ error: () => this.sincronizar() });
  }

  concluir(id: number): void {
    this.remover(id);
    this.lembreteService.processar(id).subscribe({ error: () => this.sincronizar() });
  }

  dispensar(id: number): void {
    this.remover(id);
    this.lembreteService.dispensarAviso(id).subscribe({ error: () => this.sincronizar() });
  }

  private iniciar(): void {
    this.iniciado = true;
    this.encerrar$ = new Subject<void>();
    this.socketService.escutarNotificacaoLembrete()
      .pipe(takeUntil(this.encerrar$))
      .subscribe(evento => this.aplicarEvento(evento));
    merge(fromEvent(document, 'pointerdown'), fromEvent(document, 'keydown'))
      .pipe(take(1), takeUntil(this.encerrar$))
      .subscribe(() => this.liberarAudio());
    this.sincronizar();
  }

  private sincronizar(): void {
    if (!this.iniciado || !this.authService.logado()) return;
    this.lembreteService.buscarNotificacoes().subscribe({
      next: notificacoes => this.lista.set(this.ordenar(notificacoes)),
      error: () => undefined
    });
  }

  private aplicarEvento(evento: LembreteNotificacaoSocket): void {
    if (evento.acao === 'REMOVER') {
      this.remover(evento.lembreteId);
      return;
    }
    const notificacao = evento.notificacao;
    if (!notificacao) return;
    this.lista.update(lista => this.ordenar([...lista.filter(n => n.id !== notificacao.id), notificacao]));
    this.tocarSom(`${notificacao.id}-${notificacao.etapaAviso}`);
  }

  private liberarAudio(): void {
    if (typeof AudioContext === 'undefined') return;
    this.contextoAudio ??= new AudioContext();
    if (this.contextoAudio.state === 'suspended') this.contextoAudio.resume().catch(() => undefined);
  }

  private tocarSom(chave: string): void {
    if (!this.reservarSom(chave)) return;
    this.liberarAudio();
    const contexto = this.contextoAudio;
    if (!contexto) return;
    NOTAS_SOM_LEMBRETE.forEach(({ frequencia, atraso }) => {
      const inicio = contexto.currentTime + atraso;
      const oscilador = contexto.createOscillator();
      const volume = contexto.createGain();
      oscilador.type = 'sine';
      oscilador.frequency.setValueAtTime(frequencia, inicio);
      volume.gain.setValueAtTime(GANHO_MINIMO_SOM_LEMBRETE, inicio);
      volume.gain.exponentialRampToValueAtTime(VOLUME_SOM_LEMBRETE, inicio + ATAQUE_SOM_LEMBRETE_S);
      volume.gain.exponentialRampToValueAtTime(GANHO_MINIMO_SOM_LEMBRETE, inicio + DURACAO_NOTA_SOM_LEMBRETE_S);
      oscilador.connect(volume).connect(contexto.destination);
      oscilador.start(inicio);
      oscilador.stop(inicio + DURACAO_NOTA_SOM_LEMBRETE_S);
    });
  }

  private reservarSom(chave: string): boolean {
    const chaveStorage = `${PREFIXO_CHAVE_SOM_LEMBRETE}${chave}`;
    try {
      const marcado = Number(localStorage.getItem(chaveStorage));
      if (marcado && Date.now() - marcado < JANELA_SOM_LEMBRETE_MS) return false;
      localStorage.setItem(chaveStorage, String(Date.now()));
      setTimeout(() => localStorage.removeItem(chaveStorage), JANELA_SOM_LEMBRETE_MS);
    } catch {
      return true;
    }
    return true;
  }

  private remover(id: number): void {
    this.lista.update(lista => lista.filter(n => n.id !== id));
  }

  private ordenar(lista: LembreteNotificacao[]): LembreteNotificacao[] {
    return [...lista].sort((a, b) => new Date(a.dataHora).getTime() - new Date(b.dataHora).getTime() || a.id - b.id);
  }
}
